// Frontend Net Working Capital calculation service
// Mirrors the logic from reportBuilder.ts buildNWCDetailReport function

import { AccountEntry, CategoryTemplate, AccountType } from '../../types';
import { compareMonthYear, getPeriodDays } from './calculations';

interface NWCLineItem {
  type: 'section_header' | 'category_header' | 'account' | 'category_total' | 
        'section_total' | 'calculated_total' | 'adjustment' | 'calculated_percentage';
  name: string;
  category_value?: string;
  category?: string;
  nwc_role?: string;
  account_number?: string;
  values?: number[];
  level?: number;
  section_id?: string;
  formula?: string;
  is_final?: boolean;
}

interface NWCData {
  periods: string[];
  line_items: NWCLineItem[];
}

export class FrontendNWCCalculationService {
  static calculate(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    viewType: 'monthly' | 'quarterly' | 'yearly' = 'monthly',
    netRevenueTimeSeries?: Record<string, number>
  ): NWCData {
    if (!activeTemplate || accounts.length === 0) {
      return { periods: [], line_items: [] };
    }

    // Get all available months and sort them
    const allMonths = [...new Set(accounts.map(a => a.month))].sort(compareMonthYear);
    
    // Always calculate with monthly data, let component handle view transformations
    const periods = allMonths;

    const line_items: NWCLineItem[] = [];

    // Helper functions (copied from reportBuilder.ts)
    const getCategoriesByType = (types: string[]) =>
      activeTemplate.categories.filter(cat => types.includes(cat.type)).map(cat => cat.value);

    const getCategoryDisplayName = (categoryValue: string) =>
      activeTemplate.categories.find(cat => cat.value === categoryValue)?.label || categoryValue;

    const getCategoryType = (category: string): AccountType =>
      activeTemplate.categories.find(cat => cat.value === category)?.type || 'n/a';

    const getCategoryAccountsByMonth = (category: string, month: string) =>
      accounts.filter(account => account.category === category && account.month === month);

    const calculateMonthlyTotal = (category: string, month: string): number => {
      const monthlyAccounts = getCategoryAccountsByMonth(category, month);
      const categoryType = getCategoryType(category);
      const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
      const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
      const isExpense = ['cogs', 'contra-cogs', 'operating-expense', 'non-operating-expense'].includes(categoryType);
      const isRevenue = ['revenue', 'contra-revenue'].includes(categoryType);
      
      return monthlyAccounts.reduce((total, account) => {
        if (isAsset || isContraAsset || isExpense) {
          // Assets and expenses have normal debit balances
          return total + (account.debit - account.credit);
        } else {
          // Liabilities, equity, and revenue have normal credit balances
          return total + (account.credit - account.debit);
        }
      }, 0);
    };

    const calculateSectionTotal = (types: string[], month: string): number => {
      const categories = getCategoriesByType(types);
      return categories.reduce((total, category) => total + calculateMonthlyTotal(category, month), 0);
    };

    const getNWCRole = (category: string) =>
      activeTemplate.categories.find(cat => cat.value === category)?.nwcRole || null;

    const getDefinitionalAdjustmentCategories = () =>
      activeTemplate.categories.filter(cat => 
        cat.nwcRole === 'cash' || cat.nwcRole === 'short-term-debt' || cat.nwcRole === 'debt-like'
      ).map(cat => cat.value);

    // Helper to get account display order for a category
    const getAccountDisplayOrder = (categoryValue: string): string[] => {
      const categoryAccounts = accounts.filter(acc => acc.category === categoryValue);
      const uniqueAccountNumbers = [...new Set(categoryAccounts.map(acc => acc.accountNumber))]
        .filter((acc): acc is string => typeof acc === 'string' && !!acc);
      
      return uniqueAccountNumbers.sort((a, b) => {
        const aNum = parseInt(a);
        const bNum = parseInt(b);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      });
    };

    // Build sections - Current Assets and Current Liabilities
    const nwcSections = [
      { id: 'current-assets', title: 'Current Assets', types: ['current-asset', 'contra-current-asset'] },
      { id: 'current-liabilities', title: 'Current Liabilities', types: ['current-liability', 'contra-current-liability'] }
    ];

    // Build each section
    nwcSections.forEach(section => {
      // Section header
      line_items.push({
        type: 'section_header',
        name: section.title,
        section_id: section.id,
        level: 1
      });

      const categories = getCategoriesByType(section.types);
      
      categories.forEach(categoryValue => {
        const category = activeTemplate.categories.find(cat => cat.value === categoryValue);
        if (!category) return;

        // Check if category has accounts in any period
        const hasAccounts = periods.some(month => getCategoryAccountsByMonth(categoryValue, month).length > 0);
        if (!hasAccounts) return;

        // Category header with values
        const categoryValues = periods.map(month => calculateMonthlyTotal(categoryValue, month));
        line_items.push({
          type: 'category_header',
          name: getCategoryDisplayName(categoryValue),
          category_value: categoryValue,
          nwc_role: category?.nwcRole || undefined,
          values: categoryValues,
          level: 2
        });

        // Get accounts in order
        const orderedAccountNumbers = getAccountDisplayOrder(categoryValue);

        // Process individual accounts
        orderedAccountNumbers.forEach(accountNumber => {
          const accountName = accounts.find(a => a.accountNumber === accountNumber)?.name || '';
          
          // Build monthly values array in sorted order
          const monthlyValues = periods.map(month => {
            const monthlyAccount = accounts.find(a => 
              a.accountNumber === accountNumber && 
              a.category === categoryValue && 
              a.month === month
            );
            
            if (!monthlyAccount) return 0;
            
            const categoryType = getCategoryType(categoryValue);
            const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
            const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
            
            return (isAsset || isContraAsset)
              ? monthlyAccount.debit - monthlyAccount.credit
              : monthlyAccount.credit - monthlyAccount.debit;
          });

          // Only add accounts that have some non-zero data
          if (monthlyValues.some(val => val !== 0)) {
            line_items.push({
              type: 'account',
              name: accountName,
              account_number: accountNumber,
              category: categoryValue,
              values: monthlyValues,
              level: 3
            });
          }
        });

        // Category total
        const categoryTotalValues = periods.map(month => calculateMonthlyTotal(categoryValue, month));
        if (categoryTotalValues.some(val => val !== 0)) {
          line_items.push({
            type: 'category_total',
            name: `Total ${getCategoryDisplayName(categoryValue)}`,
            category: categoryValue,
            values: categoryTotalValues,
            level: 2
          });
        }
      });

      // Section total
      const sectionTotalValues = periods.map(month => calculateSectionTotal(section.types, month));
      if (sectionTotalValues.some(val => val !== 0)) {
        line_items.push({
          type: 'section_total',
          name: `Total ${section.title}`,
          section_id: section.id,
          values: sectionTotalValues,
          level: 1
        });
      }
    });

    // Working Capital calculation
    const calculateNetWorkingCapital = (month: string): number => {
      const totalCurrentAssets = calculateSectionTotal(['current-asset', 'contra-current-asset'], month);
      const totalCurrentLiabilities = calculateSectionTotal(['current-liability', 'contra-current-liability'], month);
      return totalCurrentAssets - totalCurrentLiabilities;
    };

    const netWorkingCapitalValues = periods.map(month => calculateNetWorkingCapital(month));
    line_items.push({
      type: 'calculated_total',
      name: 'Working Capital',
      values: netWorkingCapitalValues,
      level: 0
    });

    // Definitional Adjustments
    const calculateDefinitionalAdjustments = (month: string): number => {
      const adjustmentCategories = getDefinitionalAdjustmentCategories();
      return adjustmentCategories.reduce((total, category) => {
        const role = getNWCRole(category);
        if (role === 'cash') {
          return total - calculateMonthlyTotal(category, month);
        }
        if (role === 'short-term-debt' || role === 'debt-like') {
          return total + calculateMonthlyTotal(category, month);
        }
        return total;
      }, 0);
    };

    // Cash adjustment row
    const cashCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'cash').map(cat => cat.value);
    const cashValues = periods.map(month => -cashCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0));
    line_items.push({
      type: 'adjustment',
      name: 'Less: Cash',
      nwc_role: 'cash',
      values: cashValues,
      level: 0
    });

    // Short-term Debt adjustment row
    const stdCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'short-term-debt').map(cat => cat.value);
    const stdValues = periods.map(month => stdCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0));
    line_items.push({
      type: 'adjustment',
      name: 'Plus: Short-term Debt',
      nwc_role: 'short-term-debt',
      values: stdValues,
      level: 0
    });

    // Total Definitional Adjustments
    const definitionalAdjustmentValues = periods.map(month => calculateDefinitionalAdjustments(month));
    line_items.push({
      type: 'calculated_total',
      name: 'Total Definitional Adjustments',
      values: definitionalAdjustmentValues,
      level: 0
    });

    // Net Working Capital After Definitional Adjustments
    const adjustedWorkingCapitalValues = periods.map((month, idx) => 
      netWorkingCapitalValues[idx] + definitionalAdjustmentValues[idx]
    );
    line_items.push({
      type: 'calculated_total',
      name: 'Net Working Capital After Definitional Adjustments',
      values: adjustedWorkingCapitalValues,
      level: 0
    });

    // Net Working Capital % of Net Revenue
    const calculateNetRevenue = (month: string): number => {
      const revenueCategories = activeTemplate.categories
        .filter(cat => cat.type === 'revenue' || cat.type === 'contra-revenue')
        .map(cat => cat.value);
      
      return revenueCategories.reduce((total, category) => {
        return total + calculateMonthlyTotal(category, month);
      }, 0);
    };

    // Period-aware days calculation based on monthly periods
    const getPeriodDaysForPeriod = (periodStr: string): number => {
      // Since we're always generating monthly data, use monthly period days
      return getPeriodDays(periodStr, 'monthly');
    };

    const workingCapitalPercentValues = periods.map(month => {
      const workingCapital = adjustedWorkingCapitalValues[periods.indexOf(month)];
      const netRevenue = calculateNetRevenue(month);
      
      if (netRevenue === 0) return 0;
      
      const periodDays = getPeriodDaysForPeriod(month);
      return (workingCapital / netRevenue) * periodDays / 365 * 100;
    });

    line_items.push({
      type: 'calculated_percentage',
      name: 'Net Working Capital % of Net Revenue',
      values: workingCapitalPercentValues,
      level: 0
    });

    // Working Capital Metrics Section
    line_items.push({
      type: 'section_header',
      name: 'Working Capital Metrics',
      section_id: 'working-capital-metrics',
      level: 1
    });

    // DIO - Days Inventory Outstanding
    const inventoryCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'inventory').map(cat => cat.value);
    const cogsCategories = activeTemplate.categories.filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs').map(cat => cat.value);

    const calculateDIO = (month: string): number => {
      const inventory = inventoryCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
      const cogs = cogsCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
      const periodDays = getPeriodDaysForPeriod(month);
      
      // Debug logging (commented out to reduce console spam)
      // console.log(`[NWC Debug] DIO calculation for ${month}:`, {
      //   inventory,
      //   cogs,
      //   periodDays,
      //   inventoryCategories,
      //   cogsCategories,
      //   result: cogs !== 0 ? (inventory / cogs) * periodDays : 0
      // });
      
      return cogs !== 0 ? (inventory / cogs) * periodDays : 0;
    };

    const dioValues = periods.map(month => calculateDIO(month));
    line_items.push({
      type: 'calculated_total',
      name: 'Days Inventory Outstanding (DIO)',
      values: dioValues,
      level: 2,
      formula: 'Inventory / COGS * days'
    });

    // DSO - Days Sales Outstanding  
    const arCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'accounts-receivable').map(cat => cat.value);

    const calculateDSO = (month: string): number => {
      const ar = arCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
      const netRevenue = calculateNetRevenue(month);
      const periodDays = getPeriodDaysForPeriod(month);
      
      return netRevenue !== 0 ? (ar / netRevenue) * periodDays : 0;
    };

    const dsoValues = periods.map(month => calculateDSO(month));
    line_items.push({
      type: 'calculated_total',
      name: 'Days Sales Outstanding (DSO)',
      values: dsoValues,
      level: 2,
      formula: 'AR / Net Revenue * days'
    });

    // DPO - Days Payable Outstanding
    const apCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'accounts-payable').map(cat => cat.value);

    const calculateDPO = (month: string): number => {
      const ap = apCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
      const cogs = cogsCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
      const periodDays = getPeriodDaysForPeriod(month);
      
      return cogs !== 0 ? (ap / cogs) * periodDays : 0;
    };

    const dpoValues = periods.map(month => calculateDPO(month));
    line_items.push({
      type: 'calculated_total',
      name: 'Days Payable Outstanding (DPO)',
      values: dpoValues,
      level: 2,
      formula: 'AP / COGS * days'
    });

    // CCC - Cash Conversion Cycle
    const cccValues = periods.map((month, idx) => dioValues[idx] + dsoValues[idx] - dpoValues[idx]);
    line_items.push({
      type: 'calculated_total',
      name: 'Cash Conversion Cycle',
      values: cccValues,
      level: 1,
      formula: 'DIO + DSO - DPO',
      is_final: true
    });

    return {
      periods,
      line_items
    };
  }
}
